import { ref } from 'vue';
import { useEnvProps } from '~/composables/useEnvProps';
import { PHOTO_LIMITS, ONE_MB } from '../components/writeReview/constants';

const ACCEPTABLE_IMG_FORMATS = ['png', 'jpeg', 'jpg', 'gif', 'tif', 'tiff', 'heic'] as const;
const ACCEPTABLE_IMG_FORMATS_UI = ACCEPTABLE_IMG_FORMATS.reduce((result, el, i, arr) => {
  const isComma = i > 0 && i < arr.length - 1;
  if (!result) return el.toUpperCase();
  return result + (isComma ? ', ' : ' or ') + el.toUpperCase();
}, '');

type ReviewUploadedImage = {
  url: string;
  isLoading: boolean;
  error: null;
};

/** e.g. `FileList` from `<input type="file">` or any iterable of files */
type FileIterable = Iterable<File> | ArrayLike<File>;

const useReviewImageUpload = ({ productId }: { productId: string }) => {
  const { hostlink } = useEnvProps();
  const images = ref<ReviewUploadedImage[]>([]);
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const mediaErrorMessage = ref('');

  const uploadPhotos = async (inputFiles: FileIterable) => {
    isLoading.value = true;
    error.value = null;
    const photoFilesArray = Array.from(inputFiles);

    try {
      await Promise.all(
        photoFilesArray.map(async (photoFile) => {
          const formData = new FormData();
          formData.append('data', photoFile);

          const response = await fetch(`${hostlink}/xapi/digital/v1/product/${productId}/review/photoupload`, {
            method: 'POST',
            body: formData,
            enctype: 'multipart/form-data',
            contentType: false,
            processData: false,
          });

          const data = await response.json();
          if (!response.ok || data.review.hasErrors) {
            throw new Error(data.review.errors[0].message);
          } else {
            images.value.push({ url: data.review.photos[0].sizes.normal.url, isLoading: false, error: null });
          }
        }),
      );
    } catch {
      error.value = 'Failed to upload photos. Please try again.';
    } finally {
      isLoading.value = false;
    }
  };

  const removePhoto = async (index: number) => {
    if (index === null || index === undefined) return;
    const isConfirmed = window.confirm('Are you sure you want to remove this photo?');
    if (isConfirmed) {
      isLoading.value = true;
      try {
        images.value.splice(index, 1);
        error.value = null;
      } catch {
        error.value = 'Failed to remove image';
      } finally {
        isLoading.value = false;
      }
    }
  };

  const validatePhotos = async (inputFiles: FileIterable) => {
    Array.from(inputFiles).forEach((file) => {
      const type = file.name.toLowerCase().split('.').pop();
      if (!type || !ACCEPTABLE_IMG_FORMATS.some((format) => format === type)) {
        mediaErrorMessage.value = `Image could not be uploaded. Please ensure the image is in one of the following formats: ${ACCEPTABLE_IMG_FORMATS_UI}`;
        return;
      }
      if (file.size > PHOTO_LIMITS.PHOTO_SIZE_LIMIT * ONE_MB) {
        mediaErrorMessage.value = 'Image is too large. Please ensure the file size is 5MB or less.';
        return;
      }
      // check width and height
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (e) => {
        const image = new Image();
        image.src = e.target?.result;
        image.onload = function () {
          if (this.height < PHOTO_LIMITS.MIN_PHOTO_HEIGHT || this.width < PHOTO_LIMITS.MIN_PHOTO_WIDTH) {
            mediaErrorMessage.value = 'Image is too narrow or too short. Please ensure the image is at least 100 pixels wide and 100 pixels tall.';
          }
        };
      };
    });
  };

  return {
    images,
    isLoading,
    error,
    mediaErrorMessage,
    uploadPhotos,
    removePhoto,
    validatePhotos,
  };
};

export default useReviewImageUpload;
